/**
 * Student's REPORT CARD (E2) — PURE selectors for the gradebook as seen by
 * its own owner.
 *
 * The TEACHER's gradebook is a `students × items` matrix computed by the
 * server (`use_cases::gradebook::compute_cohort_gradebook`, endpoint
 * `/api/cohorts/:id/gradebook`) which the student **cannot** request: it
 * would include their classmates' grades. But the three pieces of THEIR row
 * already travel to their device via `my_cohorts_student` — `grade_category`,
 * `grade_item` (weight transparency) and `assignment_grade` filtered by
 * `user_id` —, so their report card gets composed on the client without asking for more.
 *
 * `weightedFinal` is the PURE MIRROR of `weighted_final` in Rust (same
 * contract: absent fraction ⇒ 0, explicit `null` ⇒ item omitted,
 * `dropLowest` discards the worst without ever discarding all of them, an
 * empty category doesn't contribute, items with no category form a
 * weight-1 bucket). Keep it IN SYNC with the Rust: parity is verified with
 * the same cases in `__tests__/report-card.test.ts` — the same pattern
 * `grading/registry.ts` and `completion/registry.ts` already follow.
 *
 * What's NEW here —and doesn't exist on the server— is the **projected
 * grade**: the one that comes from weighting ONLY what's already been
 * graded. The server's final counts whatever has no grade yet as 0 (it's a
 * final, not a forecast), so halfway through the course it shows a
 * discouraging, meaningless number. The projection answers the student's
 * actual question: "given what I've done so far, how am I doing?" It's
 * obtained with the SAME function, omitting pending items just like an excused one — there is no second weighting.
 */

/** Grade sources for a `grade_item` (== `source_kind` on the server). */
export type ReportCardSourceKind = "assignment" | "auto_blocks" | "attendance" | "manual";

/** Synced `grade_category` row (snake_case, null-tolerant fields). */
export interface ReportCardCategoryRow {
  id: string;
  name?: string | null;
  weight?: number | null;
  drop_lowest?: number | null;
  position?: number | null;
}

/** Synced `grade_item` row (snake_case, null-tolerant fields). */
export interface ReportCardItemRow {
  id: string;
  name?: string | null;
  category_id?: string | null;
  source_kind?: string | null;
  source_ref?: string | null;
  max_score?: number | null;
  weight?: number | null;
  position?: number | null;
}

/** MY grade for an assignment (`assignment_grade`, own rows via sync). */
export interface ReportCardGrade {
  score: number | null;
  status: string | null;
}

/**
 * The three sources each item's fraction comes from, already resolved by
 * the caller from synced data:
 *  · `assignment`   → my `assignment_grade` indexed by `assignment_id`.
 *  · `auto_blocks`  → average of the course's `lesson_progress.score` (0..1).
 *  · `attendance`   → `cohort_enrollment.attendance_rate` (0..1).
 *  · `manual`       → fixed credit (server v1), no source.
 * `null` in the last two means "no data yet", and then the item counts as
 * PENDING for the projection (not as zero).
 */
export interface ReportCardSources {
  gradeByAssignment: ReadonlyMap<string, ReportCardGrade>;
  autoBlocksFraction: number | null;
  attendanceFraction: number | null;
}

/** A report card cell (an item with MY grade resolved). */
export interface ReportCardCell {
  itemId: string;
  name: string;
  categoryId: string | null;
  sourceKind: ReportCardSourceKind;
  /** Item's native max (to show «17 / 20»). */
  maxScore: number;
  weight: number;
  /** Native grade (0..maxScore); `null` if there is none or it's excused. */
  score: number | null;
  /** Fraction 0..1 that enters the official weighting (0 if no grade). */
  fraction: number;
  excused: boolean;
  /** Does it already have a firm grade? (decides whether it enters the projection.) */
  graded: boolean;
}

/** A report card category with its cells (weights = transparency). */
export interface ReportCardCategoryGroup {
  id: string | null;
  name: string;
  weight: number;
  dropLowest: number;
  cells: ReportCardCell[];
}

/** The student's full report card for a cohort. */
export interface ReportCard {
  categories: ReportCardCategoryGroup[];
  cells: ReportCardCell[];
  /** Official RAW final grade (fraction 0..1): pending counts as 0. */
  officialRaw: number;
  /** Raw PROJECTED grade (fraction 0..1): only what's already graded. */
  projectedRaw: number;
  /** Items with a firm grade / items that could still get one (excludes excused). */
  gradedCount: number;
  gradableCount: number;
}

/** Minimal shape of a category for weighting. */
export interface WeightedCategory {
  id: string;
  weight: number;
  dropLowest: number;
}

/** Minimal shape of an item for weighting. */
export interface WeightedItem {
  id: string;
  categoryId: string | null;
  weight: number;
}

/** Virtual bucket for items with no category (weight 1, no drops). */
const UNCATEGORIZED = " uncategorized";

const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));

/**
 * RAW final grade (fraction 0..1) — pure mirror of `weighted_final` (Rust).
 *
 * `fractions` maps `itemId → fraction`:
 *   · absent key            ⇒ 0 (no grade, counts against);
 *   · `null` value          ⇒ the item is OMITTED (excused, or pending when
 *     the projection is requested);
 *   · numeric value         ⇒ clamped to 0..1.
 */
export function weightedFinal(
  categories: readonly WeightedCategory[],
  items: readonly WeightedItem[],
  fractions: ReadonlyMap<string, number | null>,
): number {
  const catWeight = new Map<string, number>();
  const catDrop = new Map<string, number>();
  for (const c of categories) {
    catWeight.set(c.id, Math.max(0, c.weight));
    catDrop.set(c.id, Math.max(0, Math.trunc(c.dropLowest)));
  }

  // (item weight, fraction) grouped by effective category.
  const buckets = new Map<string, { weight: number; fraction: number }[]>();
  for (const item of items) {
    const raw = fractions.has(item.id) ? fractions.get(item.id) : 0;
    if (raw === null || raw === undefined) {
      continue;
    }
    const key =
      item.categoryId !== null && catWeight.has(item.categoryId) ? item.categoryId : UNCATEGORIZED;
    const bucket = buckets.get(key) ?? [];
    bucket.push({ weight: Math.max(0, item.weight), fraction: clamp01(raw) });
    buckets.set(key, bucket);
  }

  let num = 0;
  let den = 0;
  for (const [key, all] of buckets) {
    if (all.length === 0) {
      continue;
    }
    let entries = all;
    const drop = catDrop.get(key) ?? 0;
    if (drop > 0 && entries.length > 1) {
      // Keeps the `keep` with the HIGHEST fraction (never ends up with none).
      const keep = Math.max(1, entries.length - drop);
      entries = entries.toSorted((a, b) => a.fraction - b.fraction).slice(entries.length - keep);
    }
    const itemDen = entries.reduce((acc, e) => acc + e.weight, 0);
    const catScore =
      itemDen > 0
        ? entries.reduce((acc, e) => acc + e.weight * e.fraction, 0) / itemDen
        : // All items have weight 0 → simple average.
          entries.reduce((acc, e) => acc + e.fraction, 0) / entries.length;
    const cw = key === UNCATEGORIZED ? 1 : (catWeight.get(key) ?? 1);
    num += cw * catScore;
    den += cw;
  }
  return den > 0 ? clamp01(num / den) : 0;
}

/** Coerces a string to a known `source_kind` (default `manual`). */
function parseSourceKind(kind: string | null | undefined): ReportCardSourceKind {
  switch (kind) {
    case "assignment":
    case "auto_blocks":
    case "attendance": {
      return kind;
    }
    default: {
      return "manual";
    }
  }
}

/**
 * Resolves MY cell for an item — mirror of the `source_kind → fraction`
 * mapping `compute_cohort_gradebook` does for each student.
 */
function cellFor(item: ReportCardItemRow, sources: ReportCardSources): ReportCardCell {
  const sourceKind = parseSourceKind(item.source_kind);
  const maxScore = item.max_score ?? 0;
  const scale = maxScore > 0 ? maxScore : 1;

  let score: number | null = null;
  let fraction = 0;
  let excused = false;
  let graded = false;

  switch (sourceKind) {
    case "assignment": {
      const grade = sources.gradeByAssignment.get(item.source_ref ?? "");
      if (grade?.status === "excused") {
        excused = true;
      } else if (
        grade &&
        grade.score !== null &&
        (grade.status === "graded" || grade.status === "returned")
      ) {
        ({ score } = grade);
        fraction = clamp01(grade.score / scale);
        graded = true;
      }
      break;
    }
    case "auto_blocks": {
      if (sources.autoBlocksFraction !== null) {
        fraction = clamp01(sources.autoBlocksFraction);
        score = fraction * maxScore;
        graded = true;
      }
      break;
    }
    case "attendance": {
      if (sources.attendanceFraction !== null) {
        fraction = clamp01(sources.attendanceFraction);
        score = fraction * maxScore;
        graded = true;
      }
      break;
    }
    default: {
      // `manual` (server v1): the item's fixed credit.
      score = maxScore;
      fraction = 1;
      graded = true;
    }
  }

  return {
    itemId: item.id,
    name: item.name ?? "",
    categoryId: item.category_id ?? null,
    sourceKind,
    maxScore,
    weight: item.weight ?? 1,
    score,
    fraction,
    excused,
    graded,
  };
}

const byPosition = <T extends { position?: number | null; name?: string | null }>(
  a: T,
  b: T,
): number => {
  const diff = (a.position ?? 0) - (b.position ?? 0);
  return diff === 0 ? (a.name ?? "").localeCompare(b.name ?? "") : diff;
};

/**
 * Composes the student's report card from what's already synced.
 *
 * The official and projected grades come from the SAME `weightedFinal`,
 * changing only what happens with what's pending: counting it as 0
 * (official, just like the server) or omitting it (projection).
 */
export function buildReportCard(
  categoryRows: readonly ReportCardCategoryRow[],
  itemRows: readonly ReportCardItemRow[],
  sources: ReportCardSources,
): ReportCard {
  const cells = itemRows.toSorted(byPosition).map((item) => cellFor(item, sources));

  const weightedCategories: WeightedCategory[] = categoryRows.map((c) => ({
    id: c.id,
    weight: c.weight ?? 1,
    dropLowest: c.drop_lowest ?? 0,
  }));
  const weightedItems: WeightedItem[] = cells.map((cell) => ({
    id: cell.itemId,
    categoryId: cell.categoryId,
    weight: cell.weight,
  }));

  const official = new Map<string, number | null>();
  const projected = new Map<string, number | null>();
  for (const cell of cells) {
    official.set(cell.itemId, cell.excused ? null : cell.fraction);
    // Pending ⇒ out of the projection (just like an excused item).
    projected.set(cell.itemId, cell.excused || !cell.graded ? null : cell.fraction);
  }

  const groups = new Map<string | null, ReportCardCategoryGroup>();
  for (const c of categoryRows.toSorted(byPosition)) {
    groups.set(c.id, {
      id: c.id,
      name: c.name ?? "",
      weight: c.weight ?? 1,
      dropLowest: c.drop_lowest ?? 0,
      cells: [],
    });
  }
  for (const cell of cells) {
    const key = cell.categoryId !== null && groups.has(cell.categoryId) ? cell.categoryId : null;
    let group = groups.get(key);
    if (!group) {
      group = { id: null, name: "", weight: 1, dropLowest: 0, cells: [] };
      groups.set(null, group);
    }
    group.cells.push(cell);
  }

  const gradable = cells.filter((cell) => !cell.excused);
  return {
    categories: [...groups.values()].filter((g) => g.cells.length > 0),
    cells,
    officialRaw: weightedFinal(weightedCategories, weightedItems, official),
    projectedRaw: weightedFinal(weightedCategories, weightedItems, projected),
    gradedCount: gradable.filter((cell) => cell.graded).length,
    gradableCount: gradable.length,
  };
}
